import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Keyhole, GoogleLogo, ArrowsClockwise, ArrowSquareOut } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import DoorReveal from "@/components/DoorReveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1777872721345-dccb2989f646?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzYWZhcmklMjBsb2RnZXxlbnwwfHx8fDE3ODgwMTMyNDV8MA&ixlib=rb-4.1.0&q=85";

const EASYSTAY_URL = "https://easystayzm.com";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [sso, setSso] = useState(null);
  const { login, setUser, user } = useAuth();
  const navigate = useNavigate();

  const inputCls = "border-white/15 bg-white/5 text-forest-foreground placeholder:text-forest-foreground/40 focus-visible:ring-primary";
  const labelCls = "text-forest-foreground/85";

  useEffect(() => {
    // EasyStay host-dashboard deep link: session token arrives in the URL hash
    // (hash is never sent to servers or Referer headers — same pattern as Google #session_id)
    const hash = window.location.hash;
    if (hash.includes("eso_token=")) {
      const esoToken = hash.split("eso_token=")[1].split("&")[0];
      (async () => {
        try {
          const { data } = await api.post("/auth/session-import", { session_token: esoToken });
          setUser(data);
          window.history.replaceState(null, "", "/login");
          toast.success(`Welcome back, ${data.name}`);
          setOpening(true);
        } catch (err) {
          toast.error(formatApiError(err));
          window.history.replaceState(null, "", "/login");
        }
      })();
      return;
    }
    // Google sign-in returns land at "/#session_id=..." — handled by AuthCallback at App level (render-time)
    const params = new URLSearchParams(window.location.search);
    if (params.get("error")) return; // SSO bounced back without a session — show the form
    if (params.get("sso") === "easystay") sessionStorage.removeItem("esm_sso_checked"); // deep link from EasyStay: always re-probe
    if (sessionStorage.getItem("esm_sso_checked")) return;
    sessionStorage.setItem("esm_sso_checked", "1");
    // Silent SSO: the browser carries the EasyStay session cookie cross-origin (CORS-enabled),
    // so a credentialed handoff fetch recognises a logged-in EasyStay user automatically
    (async () => {
      try {
        const res = await fetch(`${EASYSTAY_URL}/api/auth/handoff?client=manager`, { credentials: "include" });
        if (res.ok) {
          const handoff = await res.json();
          if (handoff?.session_token) {
            const { data } = await api.post("/auth/session-import", { session_token: handoff.session_token });
            setUser(data);
            toast.success(`Welcome back, ${data.name}`);
            setOpening(true);
            return;
          }
        }
        setSso({ state: "none" }); // reachable, just not logged in to EasyStay
      } catch {
        // Distinguish "EasyStay down" from "cross-origin fetch blocked" (origin not in EasyStay's
        // CORS allowlist, or third-party cookies disabled) — no-cors probe resolves if reachable
        try {
          await fetch(`${EASYSTAY_URL}/api/auth/handoff?client=manager`, { mode: "no-cors" });
          setSso({ state: "none" });
        } catch {
          setSso({ state: "offline" });
        }
      }
    })();
  }, [setUser]);

  // Already authenticated (e.g. deep link from the EasyStay host dashboard) — go straight in.
  // Skipped while the door-reveal animation owns navigation after a fresh login.
  useEffect(() => {
    if (user && !opening) navigate("/", { replace: true });
  }, [user, opening, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      setOpening(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`;
  };

  const easyStaySso = async () => {
    sessionStorage.removeItem("esm_sso_checked");
    try {
      const res = await fetch(`${EASYSTAY_URL}/api/auth/handoff?client=manager`, { credentials: "include" });
      if (res.ok) {
        const handoff = await res.json();
        if (handoff?.session_token) {
          const { data } = await api.post("/auth/session-import", { session_token: handoff.session_token });
          setUser(data);
          toast.success(`Welcome back, ${data.name}`);
          setOpening(true);
          return;
        }
      }
      // No EasyStay session — authenticate on easystayzm.com; returning here auto-recognises you
      window.location.href = `${EASYSTAY_URL}/login`;
    } catch {
      // Credentialed fetch blocked (CORS allowlist / third-party cookies). EasyStay may still be
      // reachable top-level — probe no-cors; if up, send the user to sign in there instead of dead-ending
      try {
        await fetch(`${EASYSTAY_URL}/api/auth/handoff?client=manager`, { mode: "no-cors" });
        toast.info("Opening EasyStay — sign in there, then use 'Manage in EasyStay Manager' to return here.");
        window.location.href = `${EASYSTAY_URL}/login`;
      } catch {
        toast.error("Could not reach EasyStay — sign in with your EasyStay email and password below.");
      }
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:block">
        <img src={HERO_IMAGE} alt="Zambian safari lodge" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, hsl(152 42% 11% / 0.96), hsl(152 42% 17% / 0.6) 55%, hsl(152 42% 17% / 0.35))" }} />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <div className="mb-4 flex items-center gap-3">
            <img src="/logo.png" alt="EasyStay Manager" className="h-11 w-11 rounded-lg object-cover" data-testid="login-logo-icon" />
            <div className="font-heading text-xl font-bold leading-none" data-testid="login-logo">
              <span className="text-white">Easy</span><span className="text-primary">Stay</span><span className="font-heading font-bold text-emerald-400">M</span>
            </div>
          </div>
          <h1 className="font-heading max-w-md text-4xl font-bold leading-tight">
            Run your lodge with Swiss precision, Zambian warmth.
          </h1>
          <p className="mt-3 max-w-md text-sm opacity-80">
            One account across EasyStay and EasyStay Manager — rooms, bookings, invoicing and occupancy in a single front desk.
          </p>
        </div>
      </div>

      <div className="relative flex items-center justify-center overflow-hidden bg-forest px-6 py-12">
        <Keyhole size={420} weight="duotone" className="pointer-events-none absolute -bottom-24 -right-24 text-white/5" />
        <div className="relative w-full max-w-sm">
          <div className="keyhole-welcome mb-6 flex h-16 w-16 items-center justify-center rounded-full border-2 border-primary/50 bg-primary/15" data-testid="login-keyhole">
            <Keyhole size={32} weight="duotone" className="text-primary" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-forest-foreground" data-testid="login-heading">
            Sign in with EasyStay
          </h2>
          <p className="mt-1 text-sm text-forest-foreground/60">
            Use your existing EasyStay host account — no separate registration.
          </p>

          <div className="mt-8 space-y-3">
            <Button type="button" variant="outline" onClick={easyStaySso} data-testid="sso-easystay-button"
              className="w-full border-white/20 bg-white/5 text-forest-foreground transition-colors hover:border-primary hover:text-primary">
              <ArrowsClockwise size={17} className="mr-2" /> Continue with EasyStay
              {sso?.state === "offline" && <span className="ml-2 text-[10px] uppercase tracking-wider text-status-pending">offline</span>}
            </Button>
            <Button type="button" variant="outline" onClick={googleSignIn} data-testid="google-signin-button"
              className="w-full border-white/20 bg-white/5 text-forest-foreground transition-colors hover:border-primary hover:text-primary">
              <GoogleLogo size={17} className="mr-2" /> Continue with Google
            </Button>
          </div>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-forest-foreground/40">
            <span className="h-px flex-1 bg-white/10" /> or with email <span className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className={labelCls}>EasyStay email</Label>
              <Input id="email" type="email" className={inputCls} data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className={labelCls}>Password</Label>
              <Input id="password" type="password" className={inputCls} data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 transition-colors hover:bg-primary/90">
              {loading ? "Verifying with EasyStay…" : "Sign in"}
            </Button>
          </form>

          <a href={EASYSTAY_URL} target="_blank" rel="noreferrer" data-testid="easystay-register-link"
            className="mt-6 flex items-center gap-1.5 text-sm text-primary underline-offset-4 transition-colors hover:text-primary/80 hover:underline">
            New to EasyStay? Create your account on easystayzm.com <ArrowSquareOut size={13} />
          </a>
        </div>
      </div>
      {opening && <DoorReveal onDone={() => navigate("/")} />}
    </div>
  );
}
